/**
 * useSelectionBranches — merge the curated, hand-designed branch chips with the
 * DB-driven active branch set so the customer entry pages reflect admin AI Flows
 * edits (new branches appear; disabled ones disappear) with no deploy.
 *
 * Behaviour:
 *   - Curated branches keep their bespoke prompt/label/order, but only show if
 *     still active on the server (respects an admin disabling one).
 *   - Active server branches not in the curated set are appended as generic
 *     chips with a sensible default prompt.
 *   - On fetch failure / empty response, the full curated list is the fallback,
 *     so the hero never goes empty.
 */
import { useEffect, useState } from 'preact/hooks'

import { getSelectionBranches } from '../../services/selectionBranchesService.js'

export function useSelectionBranches(curated) {
  const [branches, setBranches] = useState(curated)

  useEffect(() => {
    let alive = true
    getSelectionBranches()
      .then((list) => {
        if (!alive || !Array.isArray(list) || list.length === 0) return
        const activeSlugs = new Set(list.map((b) => b.slug))
        const curatedSlugs = new Set(curated.map((c) => c.slug))
        const curatedActive = curated.filter((c) => activeSlugs.has(c.slug))
        const extras = list
          .filter((b) => b && b.slug && !curatedSlugs.has(b.slug))
          .map((b) => ({
            slug: b.slug,
            label: b.label || b.slug,
            prompt: `I'm choosing a cell for ${b.label || b.slug}.`,
          }))
        setBranches([...curatedActive, ...extras])
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  return branches
}
